// Port of SuiteModel/AgentConventions.swift (`design-docs/Agent tools, second round — design.md`): what an agent needs
// to know to write into a project — what its text is written in and the conventions of its format. Shown by MCP
// `describe_project` and, cut to its first line, by `list_projects`. The same bytes as the Swift's, pinned by
// `fixtures/agent-conventions.json` (written by AgentConventionsTests).
import { isScriptKind, projectKindName, scriptFormatName, type ProjectKind, type ScriptFormat } from './scriptFormat.ts';

/** `AgentConventions.writtenIn`: "Fountain" for a script of any kind, else "Markdown". */
export const writtenIn = (kind: ProjectKind): string => (isScriptKind(kind) ? 'Fountain' : 'Markdown');

/** `AgentConventions.formatLine`: "Stage play (UK), a musical — written in Fountain", "Manuscript — written in Markdown". */
export function agentFormatLine(kind: ProjectKind, format: ScriptFormat, musical: boolean): string {
  const name = isScriptKind(kind) ? scriptFormatName(format) + (musical ? ', a musical' : '') : projectKindName(kind);
  return `${name} — written in ${writtenIn(kind)}`;
}

/** `AgentConventions.conventions`: the conventions paragraph for a project's format. */
export function agentConventions(kind: ProjectKind, format: ScriptFormat, musical: boolean): string {
  if (!isScriptKind(kind)) return kind === 'journal' ? JOURNAL : PROSE;
  switch (format) {
    case 'multicam': return SITCOM;
    case 'stage-us': case 'stage-uk': return STAGE + (musical ? ' ' + MUSICAL : '');
    case 'radio': return RADIO;
    default: return SCREEN;
  }
}

const PROSE = 'Write Markdown: paragraphs separated by a blank line, *italic* and **bold**, and a line of *** for a '
  + 'scene break. Headings become bold lines and lists bulleted paragraphs; links to web or email addresses stay links, '
  + 'other links become their words. Each scene is its own document; a folder of documents is a chapter and a folder of '
  + 'folders a part, and compile numbers them.';

const JOURNAL = 'Write Markdown as for a book: paragraphs separated by a blank line, *italic* and **bold**, a line of *** '
  + 'for a break. A post is a top-level document (a folder groups posts); anything under a post is a section of it. Merge '
  + 'tags such as {{name}} or [FIRSTNAME] are left as written.';

const SCREEN = 'Write Fountain. A scene heading is a line starting INT. or EXT.; a character cue is a line in '
  + 'capitals, and an extension in brackets stays on it (MAYA (V.O.)); a parenthetical is a bracketed line of its own after '
  + 'the cue; the speech follows; a line starting > is a transition. *italic* and **bold** are emphasis, never a direction. '
  + 'Each scene is its own document; a folder is an act.';

const SITCOM = 'Write Fountain as for a screenplay: INT./EXT. scene headings, cues in capitals, parentheticals in '
  + 'brackets on their own line, the speech after. Scenes are lettered A, B, C from their order and an act is a folder, so '
  + 'never number or letter them in the text. Action is set in capitals and a character\'s entrances and exits are '
  + 'underlined by the layout, not by you.';

const STAGE = 'Write Fountain. A character cue is a line in capitals, and an extension in brackets stays on it '
  + '(MAYA (OFF)); a parenthetical is a bracketed line of its own after the cue; the speech follows. A paragraph that isn\'t '
  + 'speech is a stage direction: write it plainly, as the US layout puts it in brackets and the UK layout sets it in '
  + 'italics, so never bracket or italicise it yourself. *italic* inside speech is emphasis. No scene headings: each scene '
  + 'is its own document and each act a folder, and the ACT and SCENE headings are generated; a line starting Setting: or '
  + 'At rise: opens a scene\'s description.';

const MUSICAL = 'A song is a stage direction starting Song: followed by its title; the lines of its lyric start with ~.';

const RADIO = 'Write Fountain. A character cue is a line in capitals; a parenthetical is a bracketed line of its own '
  + 'after it; the speech follows. A paragraph that isn\'t speech is a technical direction — sound, music, grams — written '
  + 'plainly, as the layout sets it in capitals and underlines it. No scene headings: each scene is its own document, and '
  + 'the scenes are numbered through the play.';
